import React, { useEffect, useId, useRef, useState } from "react";

export type AccountSelectorOption = {
  account_id: string;
  account_name?: string | null;
  display_name?: string | null;
  business_name?: string | null;
  website_url?: string | null;
  website_domain?: string | null;
  website?: string | null;
  domain?: string | null;
  account_type?: string | null;
  status?: string | null;
};

type AccountSelectorProps<T extends AccountSelectorOption> = {
  accounts: T[];
  selectedAccountId: string;
  isLoading?: boolean;
  onSelect: (accountId: string) => void;
};

function accountLabel(account: AccountSelectorOption) {
  return account.account_name || account.display_name || account.business_name || "Unnamed account";
}

function accountWebsite(account: AccountSelectorOption) {
  return account.website_domain || account.website_url || account.website || account.domain || "";
}

export default function AccountSelector<T extends AccountSelectorOption>({
  accounts,
  selectedAccountId,
  isLoading = false,
  onSelect,
}: AccountSelectorProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  const selectedAccount = accounts.find((account) => account.account_id === selectedAccountId);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredAccounts = normalizedQuery
    ? accounts.filter((account) => {
        const searchableText = [
          accountLabel(account),
          accountWebsite(account),
          account.website_url,
          account.website_domain,
          account.website,
          account.domain,
          account.account_type,
          account.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();
        return searchableText.includes(normalizedQuery);
      })
    : accounts;

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function closeSelector() {
    setIsOpen(false);
    setQuery("");
    setActiveIndex(0);
    triggerRef.current?.focus();
  }

  function chooseAccount(accountId: string) {
    onSelect(accountId);
    setIsOpen(false);
    setQuery("");
    setActiveIndex(0);
    triggerRef.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeSelector();
      return;
    }

    if (event.key === "ArrowDown" && filteredAccounts.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % filteredAccounts.length);
      return;
    }

    if (event.key === "ArrowUp" && filteredAccounts.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + filteredAccounts.length) % filteredAccounts.length);
      return;
    }

    if (event.key === "Enter" && filteredAccounts[activeIndex]) {
      event.preventDefault();
      chooseAccount(filteredAccounts[activeIndex].account_id);
    }
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        disabled={isLoading}
        onClick={() => {
          setIsOpen((open) => !open);
          setQuery("");
          setActiveIndex(0);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          width: "100%",
          minHeight: 42,
          padding: "9px 12px",
          border: "1px solid #cbd5e1",
          borderRadius: 9,
          background: isLoading ? "#f8fafc" : "white",
          color: "#0f172a",
          textAlign: "left",
          fontSize: 14,
          cursor: isLoading ? "not-allowed" : "pointer",
          opacity: isLoading ? 0.7 : 1,
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {isLoading ? "Loading accounts..." : selectedAccount ? accountLabel(selectedAccount) : "Choose an existing account"}
          {selectedAccount && (selectedAccount.account_type || selectedAccount.status) ? (
            <span style={{ color: "#64748b", fontWeight: 500 }}>
              {` · ${[selectedAccount.account_type, selectedAccount.status].filter(Boolean).join(" · ")}`}
            </span>
          ) : null}
        </span>
        <span aria-hidden="true" style={{ color: selectedAccount ? "#2563eb" : "#64748b", fontSize: 12, fontWeight: 700 }}>
          {selectedAccount ? "Change" : isOpen ? "Close" : "Select"}
        </span>
      </button>

      {selectedAccount ? (
        <div style={{ marginTop: 6, color: "#475569", fontSize: 12 }} aria-live="polite">
          Selected account: {accountLabel(selectedAccount)}
          {accountWebsite(selectedAccount) ? ` · ${accountWebsite(selectedAccount)}` : ""}
          {` · ${selectedAccount.account_id}`}
        </div>
      ) : null}

      {isOpen ? (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            padding: 8,
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            background: "white",
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.14)",
          }}
        >
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-label="Find existing account"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={!isLoading && filteredAccounts[activeIndex] ? `${listId}-option-${activeIndex}` : undefined}
            autoComplete="off"
            disabled={isLoading}
            placeholder="Search name, website, type, or status"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              boxSizing: "border-box",
              width: "100%",
              padding: "9px 10px",
              border: "1px solid #cbd5e1",
              borderRadius: 7,
              color: "#0f172a",
              fontSize: 13,
            }}
          />
          <div
            id={listId}
            role="listbox"
            aria-label="Accounts"
            style={{ display: "grid", gap: 4, maxHeight: 240, overflowY: "auto", marginTop: 6 }}
          >
            {isLoading ? (
              <div role="status" style={{ padding: 9, color: "#64748b", fontSize: 13 }}>Loading accounts...</div>
            ) : filteredAccounts.length ? (
              filteredAccounts.map((account, index) => (
                <div
                  key={account.account_id}
                  id={`${listId}-option-${index}`}
                  role="option"
                  aria-selected={account.account_id === selectedAccountId}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => chooseAccount(account.account_id)}
                  style={{
                    display: "grid",
                    gap: 2,
                    padding: "8px 10px",
                    borderRadius: 7,
                    background: index === activeIndex ? "#eff6ff" : "white",
                    color: "#0f172a",
                    cursor: "pointer",
                    outline: account.account_id === selectedAccountId ? "1px solid #93c5fd" : "none",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{accountLabel(account)}</span>
                  <span style={{ display: "flex", gap: 8, flexWrap: "wrap", color: "#64748b", fontSize: 11 }}>
                    {accountWebsite(account) ? <span>{accountWebsite(account)}</span> : null}
                    {account.account_type ? <span>{account.account_type}</span> : null}
                    {account.status ? <span>{account.status}</span> : null}
                  </span>
                </div>
              ))
            ) : (
              <div role="status" style={{ padding: 9, color: "#64748b", fontSize: 13 }}>
                No matching accounts found.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
